<!-- FuelTanks.vue
  Componente para mostrar uno o varios tanques de combustible (porcentaje y litros).
  - Muestra varios tanques en grid responsive
  - Barra de nivel vertical con “onda” sutil
  - Estados: ok / warn / crit
  - Línea mínima punteada roja (según t.minimo)
  - Color del líquido viene de BD: t.color
-->
<template>
  <div class="tanks" v-if="normalized.length">
    <div class="card card-borde">
      <div class="card-body p-0">
        <table class="table table-striped table-valign-middle table-dashboard">
          <thead>
            <tr>
              <th>
               <i class="fa-solid fa-gas-pump"></i>
                Inventario de Combustible
              </th>
            </tr>
          </thead>
        </table>
      </div>

      <div class="card-body">
        <div class="tanks-grid">
          <div class="tank-item" v-for="t in normalized" :key="t._id">
            <!-- Lado izquierdo: tanque visual -->
            <div class="tank-visual">
              <div class="tank-shell" :class="levelClass(t)">
                <div class="tank-top"></div>

                <!-- Línea mínima punteada roja -->
                <div v-if="t.minimo !== null && t.capacidad > 0" class="tank-min-line" :style="minLineStyle(t)"
                  title="Mínimo"></div>

                <!-- fill (nivel) -->
                <div class="tank-fill" :style="fillStyle(t)">
                  <div class="tank-wave"></div>
                  <div class="tank-gloss"></div>
                </div>

                <div class="tank-bottom"></div>

                <!-- porcentaje centrado -->
                <div class="tank-percent font-oswald">
                  {{ pctText(t) }}<span style="font-size: 11px;">%</span>
                </div>
              </div>

              <div class="tank-uom">
                {{ t.unidad }}
              </div>
            </div>

            <!-- Lado derecho: datos -->
            <div class="tank-meta">
              <div class="tank-label font-oswald">
                 {{ t._id }} - {{ t.nombre }}
              </div>
              <div class="tank-name">
                <span class="tank-product font-oswald">{{ t.producto }}</span>
              </div>

              <div class="tank-row">
                <span class="k">Disponible</span>
                <span class="v font-oswald">
                  {{ fmtLiters(t.actual) }} {{ t.unidad }}
                </span>
              </div>

              <div class="tank-row">
                <span class="k">Máxima</span>
                <span class="v font-oswald">
                  {{ fmtLiters(t.capacidad) }} {{ t.unidad }}
                </span>
              </div>

              <div class="tank-row" v-if="t.minimo !== null">
                <span class="k">Mínimo</span>
                <span class="v font-oswald">
                  {{ fmtLiters(t.minimo) }} {{ t.unidad }}
                </span>
              </div>

              <div class="tank-row" v-if="t.actualizacion">
                <span class="i">{{ t.actualizacion }}</span>
                <span class="k">
                  <span class="badge badge-ok" v-if="levelClass(t) === 'ok'">OPERATIVO</span>
                  <span class="badge badge-warn" v-else-if="levelClass(t) === 'warn'">BAJO</span>
                  <span class="badge badge-crit" v-else>CRÍTICO</span>
                </span>
              </div>

              <div class="tank-row" v-else>
                <span class="i">&nbsp;</span>
                <span class="k">
                  <span class="badge badge-ok" v-if="levelClass(t) === 'ok'">OPERATIVO</span>
                  <span class="badge badge-warn" v-else-if="levelClass(t) === 'warn'">BAJO</span>
                  <span class="badge badge-crit" v-else>CRÍTICO</span>
                </span>
              </div>
            </div>
          </div>

          <div class="empty" v-if="!normalized.length">
            Sin tanques configurados
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GLOBAL from "../Global.js";

export default {
  name: "FuelTanks",
  props: {
    // Array de tanques:
    // [{ id, nombre, producto, capacidad, actual, minimo?, unidad?, updated_at?, color? }]
    tanks: { type: Array, default: () => [] },

    // Umbrales por defecto (en % si NO hay mínimo definido)
    warnPct: { type: Number, default: 25 },
    critPct: { type: Number, default: 10 },
  },
  data() {
    return { GLOBAL };
  },
  computed: {
    normalized() {
      const arr = Array.isArray(this.tanks) ? this.tanks : [];
      return arr.map((t, idx) => {
        const capacidad = Number(t.capacidad || 0);
        const actual = Number(t.actual || 0);

        let minimo = t.minimo;
        minimo = minimo === undefined || minimo === "" ? null : Number(minimo);

        const unidad = String(t.unidad || "L");
        const nombre = String(t.nombre || `Tanque ${idx + 1}`);
        const producto = String(t.producto || t.producto_nombre || "—");

        // Color desde BD (ej: "#00a86b", "#eb0000", "rgba(0,123,255,.9)")
        const color = String(t.color || "").trim();

        const updated =
          t.updated_at || t.actualizado || t.fecha || t.updatedAt || null;

        return {
          _id: t.id || t._id || `${nombre}-${producto}-${idx}`,
          nombre,
          producto,
          capacidad: capacidad >= 0 ? capacidad : 0,
          actual: actual >= 0 ? actual : 0,
          minimo: minimo !== null && minimo >= 0 ? minimo : null,
          unidad,
          color,
          actualizacion: updated ? String(updated) : "",
        };
      });
    },
  },
  methods: {
    // % crudo (0..100)
    pctRaw(t) {
      if (!t.capacidad) return 0;
      const p = (t.actual / t.capacidad) * 100;
      return Math.max(0, Math.min(100, p));
    },

    // texto con 2 decimales fijos
    pctText(t) {
      return this.pctRaw(t).toFixed(2);
    },

    // number con 2 decimales
    pct(t) {
      return Number(this.pctRaw(t).toFixed(2));
    },

    // Estado ok/warn/crit:
    // - si hay mínimo (litros): compara contra mínimo
    // - si no hay mínimo: usa % por defecto
    levelClass(t) {
      const p = this.pct(t);

      if (t.minimo !== null) {
        if (t.actual <= t.minimo * 0.75) return "crit";
        if (t.actual <= t.minimo) return "warn";
        return "ok";
      }

      if (p <= this.critPct) return "crit";
      if (p <= this.warnPct) return "warn";
      return "ok";
    },

    // Estilo del líquido:
    // - altura según % actual
    // - color desde BD (t.color) si viene; si no, lo define el CSS por estado
    fillStyle(t) {
      const p = this.pct(t);
      const style = { height: `${p}%` };

      if (t.color) {
        // si el color viene plano, le damos un gradiente suave usando el mismo color
        style.background = `linear-gradient(180deg, ${t.color}, ${t.color})`;
      }

      return style;
    },

    // Línea mínima punteada: bottom% = (minimo/capacidad)*100
    minLineStyle(t) {
      if (!t.capacidad || t.minimo === null) return {};
      const pctMin = Math.max(0, Math.min(100, (t.minimo / t.capacidad) * 100));
      return { bottom: `${pctMin}%` };
    },

    fmtLiters(n) {
      const num = Number(n || 0);
      if (this.GLOBAL?.FORMATO_NUMBER_BASIC) return this.GLOBAL.FORMATO_NUMBER_BASIC(num);
      return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(num);
    },
  },
};
</script>

<style scoped>
/* =========================
   CONTENEDOR GENERAL
   ========================= */
.tanks {
  width: 100%;
}

/* =========================
   GRID RESPONSIVE
   Desktop: 3
   Tablet: 2
   Móvil: 1
   ========================= */
.tanks-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  /* Desktop */
  gap: 14px;
}

@media (max-width: 1400px) {
  .tanks-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    /* Tablet */
  }
}

@media (max-width: 768px) {
  .tanks-grid {
    grid-template-columns: 1fr;
    /* Móvil */
  }
}

/* =========================
   ITEM TANQUE
   ========================= */
.tank-item {
  display: flex;
  gap: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 12px;
  background: #fff;
}

/* =========================
   VISUAL DEL TANQUE
   ========================= */
.tank-visual {
  width: 110px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.tank-shell {
  position: relative;
  width: 84px;
  height: 150px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.02));
  border: 1px solid rgb(164, 164, 164);
  overflow: hidden;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
}

/* Tapa superior e inferior */
.tank-top,
.tank-bottom {
  position: absolute;
  left: 0;
  right: 0;
  height: 14px;
  background: rgba(0, 0, 0, 0.08);
  z-index: 3;
}

.tank-top {
  top: 0;
}

.tank-bottom {
  bottom: 0;
}

/* =========================
   CONTENIDO (LÍQUIDO)
   ========================= */
.tank-fill {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  overflow: hidden;
}

/* Estados cuando NO viene color desde BD */
.tank-shell.ok .tank-fill {
  background: linear-gradient(180deg, rgba(0, 168, 107, 0.9), rgba(0, 168, 107, 0.65));
}

.tank-shell.warn .tank-fill {
  background: linear-gradient(180deg, rgba(255, 193, 7, 0.95), rgba(255, 193, 7, 0.65));
}

.tank-shell.crit .tank-fill {
  background: linear-gradient(180deg, rgba(220, 53, 69, 0.95), rgba(220, 53, 69, 0.65));
}

/* Brillo */
.tank-gloss {
  position: absolute;
  left: 10px;
  top: 10px;
  bottom: 10px;
  width: 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.34);
  filter: blur(0.2px);
}

/* Onda */
.tank-wave {
  position: absolute;
  left: -20%;
  top: -10px;
  width: 140%;
  height: 20px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  animation: wave 2.8s ease-in-out infinite;
}

@keyframes wave {
  0% {
    transform: translateX(-10px);
    opacity: 0.2;
  }

  50% {
    transform: translateX(10px);
    opacity: 0.32;
  }

  100% {
    transform: translateX(-10px);
    opacity: 0.2;
  }
}

/* =========================
   PORCENTAJE CENTRADO
   ========================= */
.tank-percent {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 6;
  text-align: center;
  font-weight: 800;
  font-size: 17px;
  color: rgba(0, 0, 0, 0.75);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.35);
}

/* Unidad */
.tank-uom {
  font-size: 12px;
  opacity: 0.75;
}

/* =========================
   LÍNEA MÍNIMA
   ========================= */
.tank-min-line {
  position: absolute;
  left: 0px;
  right: 0px;
  border-top: 2px solid rgb(164, 164, 164);
  z-index: 5;
  pointer-events: none;
}

/* =========================
   META / TEXTO
   ========================= */
.tank-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.tank-label { 
  font-size: 12px;  
}


.tank-name {
  font-weight: 800;
  font-size: 18px;
  line-height: 1;
}

.tank-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px dashed rgba(0, 0, 0, 0.08);
  padding-top: 6px;
}

.tank-row:first-of-type {
  border-top: none;
  padding-top: 0;
}

.tank-row .k {
  font-size: 12px;
  opacity: 0.7;
  margin-top: -5px;
}

.tank-row .v {
  font-weight: 700;
  font-size: 16px;
  white-space: nowrap;
}

.tank-row .i {
  font-size: 12px;
  opacity: 0.7;
}

/* =========================
   BADGES
   ========================= */
.badge {
  padding: 4px 8px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
}

.badge-ok {
  background: rgb(0, 138, 46);
  color: rgb(255, 255, 255);
}

.badge-warn {
  background: rgba(255, 193, 7, 0.18);
  color: rgba(168, 126, 0, 1);
}

.badge-crit {
  background: rgb(255, 0, 0);
  color: rgb(255, 255, 255);
}

/* =========================
   VACÍO
   ========================= */
.empty {
  grid-column: 1 / -1;
  padding: 14px;
  text-align: center;
  opacity: 0.7;
  border: 1px dashed rgba(0, 0, 0, 0.18);
  border-radius: 10px;
}
</style>
